import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { apiGet, apiGetBlob, apiDelete } from '../api';
import { useT } from '../i18n';
import DashboardLayout from '../components/DashboardLayout';

const PAGE_SIZE = 50;

export default function HomePage() {
  const navigate = useNavigate();
  const accessToken = useStore((s) => s.accessToken);
  const t = useT();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const loadProjects = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await apiGet(`/api/v1/projects/my?page=${p}&size=${PAGE_SIZE}`);
      const data = await res.json();
      if (data.success) {
        const payload = data.data || {};
        setProjects(payload.content || payload || []);
        const meta = payload.page || payload;
        setTotalPages(meta.totalPages ?? 0);
        setTotalElements(meta.totalElements ?? 0);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const res = await apiGet('/api/v1/users/me');
      const data = await res.json();
      if (data.success && data.data?.name) {
        setUserName(data.data.name);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!accessToken) { navigate('/'); return; }
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    loadProjects(page);
  }, [accessToken, page, loadProjects]);

  const handleNewProject = () => navigate('/editor');
  const handleEditDraft = (draftId) => navigate(`/editor/${draftId}`);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = (projectId, projectName) => {
    setDeleteConfirm({ id: projectId, name: projectName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await apiDelete(`/api/v1/projects/${deleteConfirm.id}`);
      // If this was the last item on a non-first page, jump to previous page.
      if (projects.length === 1 && page > 0) {
        setPage((p) => p - 1);
      } else {
        loadProjects(page);
      }
    } catch { /* ignore */ }
    setDeleting(false);
    setDeleteConfirm(null);
  };

  const handleDownload = async (projectId, projectJson) => {
    try {
      const blob = await apiGetBlob(`/api/v1/projects/${projectId}/download`);
      let name = 'project';
      try {
        const parsed = JSON.parse(projectJson);
        name = parsed?.project?.name || parsed?.projectName || 'project';
      } catch { /* ignore */ }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${name}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { /* ignore */ }
  };

  const parseProjectName = (jsonStr) => {
    try {
      const parsed = JSON.parse(jsonStr);
      return parsed?.project?.name || parsed?.projectName || 'Untitled';
    } catch {
      return 'Untitled';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <DashboardLayout>

      {deleteConfirm && (
        <div className="confirm-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="#f85149" strokeWidth="2.5" fill="rgba(248,81,73,0.08)" />
                <path d="M24 14v12" stroke="#f85149" strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="24" cy="32" r="1.5" fill="#f85149" />
              </svg>
            </div>
            <h3 className="confirm-title">{t('delete.title')}</h3>
            <p className="confirm-desc">
              {t('delete.confirm')} <strong>"{deleteConfirm.name}"</strong>? {t('delete.undone')}
            </p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                {t('profile.cancel')}
              </button>
              <button className="confirm-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? t('delete.deleting') : t('delete.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="home-content">
        <div className="feedback-banner-compact" onClick={() => navigate('/feedback')}>
          <div className="feedback-banner-left">
            <span className="feedback-banner-badge">BETA</span>
            <span className="feedback-banner-title">{t('feedback.bannerTitle')}</span>
            <span className="feedback-banner-desc">{t('feedback.bannerDesc')}</span>
          </div>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M7 4l6 6-6 6" />
          </svg>
        </div>

        <div className="home-header">
          <div>
            <h1 className="home-title">{t('home.hey')}{userName ? `, ${userName}` : ''} &#128075;</h1>
            <p className="home-subtitle">{t('home.subtitle')}</p>
          </div>
          <button className="home-new-btn" onClick={handleNewProject}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M8 3v10M3 8h10" />
            </svg>
            {t('home.newProject')}
          </button>
        </div>

        {loading ? (
          <div className="home-loading">{t('home.loading')}</div>
        ) : projects.length === 0 ? (
          <div className="home-empty">
            <p>{t('home.noProjects')}</p>
            <button className="home-empty-btn" onClick={handleNewProject}>{t('home.createFirst')}</button>
          </div>
        ) : (
          <>
          <div className="home-grid">
            {[...projects].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)).map((p) => (
              <div key={p.id} className={`project-card ${p.generated ? '' : 'draft-card'}`}>
                <div className="project-card-header">
                  <div className={`project-card-icon ${p.generated ? '' : 'draft-icon'}`}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="2" width="14" height="16" rx="2" />
                      <path d="M7 6h6M7 10h6M7 14h3" />
                    </svg>
                  </div>
                  <span className={`project-badge ${p.generated ? (p.expired ? 'expired' : 'active') : 'draft'}`}>
                    {p.generated ? (p.expired ? t('home.expired') : t('home.generated')) : t('home.draft')}
                  </span>
                  <button
                    className="project-delete-btn"
                    onClick={(e) => { e.stopPropagation(); handleDelete(p.id, parseProjectName(p.projectJson)); }}
                    title="Delete project"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M3 3l8 8M11 3l-8 8" />
                    </svg>
                  </button>
                </div>
                <h3 className="project-card-name">{parseProjectName(p.projectJson)}</h3>
                <span className="project-card-date">Last updated: {formatDate(p.updatedAt || p.createdAt)}</span>
                <div className="project-card-actions two-actions">
                  {p.generated && !p.expired && (
                    <button className="project-action-btn download" onClick={() => handleDownload(p.id, p.projectJson)}>
                      {t('home.download')}
                    </button>
                  )}
                  <button className="project-action-btn edit" onClick={() => handleEditDraft(p.id)}>
                    {p.generated ? t('home.edit') : t('home.continue')}
                  </button>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="home-pager">
              <button
                className="admin-page-btn"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                {t('feedback.prev')}
              </button>
              <span className="admin-page-info">{page + 1} / {totalPages}</span>
              <button
                className="admin-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                {t('feedback.next')}
              </button>
              <span className="admin-page-info" style={{ marginLeft: 12 }}>
                {totalElements} {t('admin.totalRows')}
              </span>
            </div>
          )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
